import { Link } from '@tanstack/react-router'
import {
  SidebarShell,
  SidebarList,
  SidebarNavLink,
  SidebarExternalLink,
  SidebarCluster,
} from '@region13/ui'
import { FIELDS_NAV } from '../lib/nav'

const ROUTED_ITEMS = FIELDS_NAV.items.filter((i) => !i.external)
const EXTERNAL_ITEMS = FIELDS_NAV.items.filter((i) => i.external)

/** Left nav on desktop and a phone drawer below md, the shared platform sidebar.
 *
 *  This surface had NO navigation until now: `/` was the map editor, so the shell
 *  around it held only a login screen and a redirect, and `.impeccable.md`
 *  recorded the missing sidebar as known drift. Once `/` became a landing page
 *  the tool grew to five levels with breadcrumbs as the only way around, which is
 *  what made the drift worth paying off.
 *
 *  The map editor is a plain <a>, not a <Link>: it is a separate vanilla-JS app
 *  served by the worker at /map, outside this router. It sits last and separated
 *  because leaving this shell is a bigger move than changing pages inside it. */
export function Sidebar({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  return (
    <SidebarShell
      brand={{
        logoSrc: '/logo.svg',
        // One word, per the platform convention (Scheduler / Console / Coach /
        // Referee). The editor's own "FIELD MANAGEMENT" masthead is a wordmark,
        // not a subtitle, so it does not set the precedent here.
        subtitle: 'Fields',
        home: (c) => <Link to="/">{c}</Link>,
      }}
      storageKey="r13:fields-sidebar-collapsed"
      identity={email}
      signOut={{ onClick: onSignOut }}
      build={String(import.meta.env.VITE_BUILD_ID ?? 'dev')}
    >
      <SidebarList>
        {ROUTED_ITEMS.map((item) => (
          <SidebarNavLink
            key={item.to}
            icon={item.icon}
            label={item.label}
            render={(p) => (
              <Link
                to={item.to as '/' | '/venues'}
                activeOptions={{ exact: item.to === '/' }}
                {...p}
              />
            )}
          />
        ))}
      </SidebarList>
      <SidebarCluster>
        {EXTERNAL_ITEMS.map((item) => (
          <SidebarExternalLink key={item.to} href={item.to} icon={item.icon} label={item.label} />
        ))}
      </SidebarCluster>
    </SidebarShell>
  )
}
